import React, { useMemo } from "react";
import { Dataset, Datum } from "../../result-radar/types";
import { D3Radar } from "../../result-radar/D3Radar";
import { useAppDispatch, useAppSelector } from "#store";
import { Interpretation, MetadataTable } from "#/services/pilotService/types";
import { useQueries, useQuery, UseQueryResult } from "react-query";
import { tableService } from "#services/pilotService";
import { Caption, H5, P } from "#/components/Common/headings";
import { computeQueryRelevance } from "#/util/columnSorting";
import { tokenize } from "#/util/stringUtils";
import { scaleLinear, ScaleLinear } from "d3";
import {
    selectEnvServiceUrl,
    selectUiHistoryLatestInterpretationsByDate,
    selectUiQuery,
    selectUiUserId,
} from "#selectors";
import { addHistories, InteractionType } from "#store/ui";
import { formatNumber } from "@inode/multi-table-explorer";
import { Routes } from "#/config";
import { generatePath } from "react-router";
import { Link } from "#/components/Common/Link";
import { useDebouncedValue } from "rooks";
import { Box } from "@mui/material";

type RadarDatum = {
    interpretation: Interpretation;
    tableData: MetadataTable;
};

const colorAccessor = (d: RadarDatum) =>
    d.tableData.metadata.relevancies.reduce((p, c) => p + c, 0);

export const RadarContainer = (props: {
    interpretations: Interpretation[];
    hideSelectionDetails?: boolean;
}): JSX.Element => {
    // props
    const { interpretations, hideSelectionDetails = false } = props;

    // contexts
    const dispatch = useAppDispatch();

    const serviceUrl = useAppSelector(selectEnvServiceUrl);
    const userId = useAppSelector(selectUiUserId);
    const query = useAppSelector(selectUiQuery);
    const userHighlights: Interpretation[] = useAppSelector(
        selectUiHistoryLatestInterpretationsByDate
    );
    const userHighlightIds = userHighlights.map(x => x.id);

    // internal and derived states
    const sortedTableIds = useMemo(() => {
        return interpretations.map(x => x.table).sort((a, b) => a - b);
    }, [interpretations]);

    const [debouncedSortedTableIds] = useDebouncedValue(sortedTableIds, 500);

    const projection = useQuery(["projection", debouncedSortedTableIds], () => {
        return tableService(serviceUrl, userId).getProjection(
            debouncedSortedTableIds ?? []
        );
    });

    const tableDatas: UseQueryResult<MetadataTable, any>[] = useQueries(
        interpretations.map(i => {
            return {
                queryKey: ["table", i.table],
                queryFn: () =>
                    tableService(serviceUrl, userId).getTableData(i.table),
            };
        })
    );

    const dataset: Dataset<RadarDatum> = React.useMemo(() => {
        if (projection.isFetched && projection.data) {
            if (interpretations.length !== projection.data.length) {
                console.log(
                    "interpretations.length != projection.data.length",
                    interpretations.length,
                    projection.data.length
                );
                return [];
            }
            return interpretations.map(
                (interpretation: Interpretation, idx) => {
                    const tableData: UseQueryResult<MetadataTable, any> =
                        tableDatas[idx];
                    if (tableData.isFetched && tableData.data) {
                        let radius = 0,
                            angle = 0;
                        const table = tableData.data;
                        radius = computeQueryRelevance(
                            table.metadata.caption ?? "",
                            tokenize(query)
                        );

                        const v = projection.data[idx].values;
                        if (v && v.length > 0 && v[0]) {
                            angle = v[0];
                        }
                        return {
                            radius: radius,
                            angle: angle,
                            id: interpretation.id,
                            highlighted: userHighlightIds.includes(
                                interpretation.id
                            ),
                            content: {
                                interpretation,
                                tableData: tableData.data,
                            },
                        };
                    } else {
                        return {
                            radius: 1,
                            angle: 1,
                            id: interpretation.id,
                            highlighted: userHighlightIds.includes(
                                interpretation.id
                            ),
                            content: {
                                interpretation,
                                tableData: {
                                    data: [],
                                    metadata: {
                                        columns: [],
                                        caption: "",
                                        sourceQuery: "",
                                        classes: [],
                                        descriptors: [],
                                        dateformats: [],
                                        labels: [],
                                        prefixSimilarityMatrix: [],
                                        redundancyMatrix: [],
                                        relevancies: [],
                                        relation: "",
                                        types: [],
                                    },
                                    columns: [],
                                },
                            },
                        };
                    }
                }
            );
        } else {
            return [];
        }
    }, [interpretations, tableDatas, projection, query, userHighlightIds]);

    const selection = React.useMemo(() => {
        return dataset.filter(x => userHighlightIds.includes(Number(x.id)));
    }, [dataset, userHighlightIds]);

    const colorScale: ScaleLinear<number, number> = React.useMemo(() => {
        const relevancySum = dataset.map(x => colorAccessor(x.content));
        const extent = [Math.min(...relevancySum), Math.max(...relevancySum)];

        return scaleLinear().domain(extent);
    }, [dataset]);

    const selectionInfo = React.useMemo(() => {
        if (!hideSelectionDetails && selection) {
            return (
                <div>
                    <Caption>{selection.length} elements highlighted:</Caption>
                    {selection.map(value => {
                        return (
                            <div key={value.id}>
                                <Link
                                    to={generatePath(
                                        Routes.interpretation.path,
                                        {
                                            id: String(
                                                value.content.interpretation.id
                                            ),
                                        }
                                    )}
                                >
                                    <H5>
                                        Interpretation{" "}
                                        {value.content.interpretation.id}
                                    </H5>
                                </Link>
                                <P>
                                    {value.content.tableData.metadata.caption}
                                </P>
                                <Caption>
                                    {formatNumber(
                                        value.content.tableData.metadata.relevancies.reduce(
                                            (a, b) => a + b,
                                            0
                                        )
                                    )}{" "}
                                    (color/inf density) //{" "}
                                    {formatNumber(value.angle)} (angle) //{" "}
                                    {formatNumber(value.radius)} (radius/qrel)
                                </Caption>
                            </div>
                        );
                    })}
                </div>
            );
        } else {
            return null;
        }
    }, [hideSelectionDetails, selection]);

    if (!tableDatas.every(x => x.isFetched)) {
        return (
            <>
                <D3Radar<RadarDatum> data={[]} />

                {/*<CircularProgress*/}
                {/*    variant={"determinate"}*/}
                {/*    size={"100%"}*/}
                {/*    value={*/}
                {/*        (100 * tableDatas.filter(x => x.isFetched).length) /*/}
                {/*        tableDatas.length*/}
                {/*    }*/}
                {/*    color={"info"}*/}
                {/*/>*/}
                <Caption>
                    {tableDatas.filter(x => x.isFetched).length} /{" "}
                    {tableDatas.length}
                </Caption>
                <P>
                    This radar provides an overview over the relevance of
                    interpretations to you query as well as the similarity of
                    interpretations among each other. Each dot represents a
                    found interpretation. The closer dots are to the center, the
                    higher their relevance score to your query is. Similar
                    tables are placed at a similar angle. This should help you
                    to judge groups of interpretations with less effort than by
                    judging them one-by-one.
                </P>
            </>
        );
    } else {
        return (
            <>
                <D3Radar<RadarDatum>
                    data={dataset}
                    onClick={(datum: Datum<RadarDatum[]>) => {
                        dispatch(
                            addHistories(
                                datum.content.map(x => x.interpretation),
                                InteractionType.HIGHLIGHT
                            )
                        );
                    }}
                    colorScale={(data: Datum<RadarDatum[]>) => {
                        return Math.max(
                            ...data.content.map(datum => {
                                return colorScale(
                                    datum.tableData.metadata.relevancies.reduce(
                                        (p, c) => p + c,
                                        0
                                    )
                                );
                            })
                        );
                    }}
                    tooltipComponent={datum => {
                        return (
                            <>
                                <Box sx={{ padding: "2rem" }}>
                                    <P>{datum.id}</P>
                                    contains {datum.content.length} points:{" "}
                                    {datum.content.map(
                                        x => x.interpretation.id
                                    )}
                                </Box>
                            </>
                        );
                    }}
                />
                <Caption>
                    {tableDatas.filter(x => x.isFetched).length} /{" "}
                    {tableDatas.length}
                </Caption>
                {selectionInfo}
            </>
        );
    }
};
